import React, {useCallback, useMemo, useState} from 'react';
import assert from 'assert-ts';

import {useTheme} from '../../theme';
import {Icon} from '../icons';
import {IconNames} from '../icons/types';
import {BaseLayout, Layout} from '../layout';
import {BaseInput} from './BaseInput';
import {Spinner} from '../spinner';
import {Text} from '../text';

import {
  EnhancementSide,
  InputIconSize,
  InputProps,
  InputSize,
  InputState,
} from './types';
import {
  mapInputToBaseProperties,
  mapContainerToBaseProperties,
} from './functions';
import {IconLayoutMap, InputLayoutMap, InputStyleMap} from './styles';
import {Avatar} from '../avatar';
import {useNamedChildren} from './hooks';
import {LeftEnhancerName, RightEnhancerName} from './components';

export const Input: React.FC<InputProps> = ({
  children,
  onChange,
  onFocus,
  onBlur,
  leftOnPress,
  rightOnPress,
  inputWidth,
  ...props
}) => {
  const [focus, setFocus] = useState(false);
  const theme = useTheme();

  const {LeftEnhancer, RightEnhancer} = useNamedChildren(children, [
    LeftEnhancerName,
    RightEnhancerName,
  ]);

  const propsWithDefaults = useMemo(() => {
    const {
      round = false,
      state = 'Default',
      clear = false,
      size = 'Default',
      leftIconSize = '24',
      rightIconSize = '24',
      leftIconFilled = false,
      rightIconFilled = false,
      leftSpinner = false,
      rightSpinner = false,
      ...rest
    } = props;
    return {
      round,
      state,
      clear,
      size,
      leftIconSize,
      rightIconSize,
      leftIconFilled,
      rightIconFilled,
      leftSpinner,
      rightSpinner,
      ...rest,
    };
  }, [props]);

  const {
    value,
    placeholder,
    state,
    size,
    leftIcon,
    rightIcon,
    leftIconSize,
    rightIconSize,
    leftIconFilled,
    rightIconFilled,
    leftSpinner,
    rightSpinner,
    leftText,
    rightText,
    leftAvatarUri,
    leftAvatarName,
    rightAvatarUri,
    rightAvatarName,
  } = propsWithDefaults;

  const handleOnFocus = useCallback(() => {
    setFocus(true);
    onFocus && onFocus();
  }, [onFocus]);

  const handleOnBlur = useCallback(() => {
    setFocus(false);
    onBlur && onBlur();
  }, [onBlur]);

  const baseInputContainerProps = useMemo(
    () => mapContainerToBaseProperties(theme, focus, propsWithDefaults),
    [theme, focus, propsWithDefaults],
  );

  const baseInputProps = useMemo(
    () => mapInputToBaseProperties(theme, propsWithDefaults),
    [theme, propsWithDefaults],
  );

  const renderStaticText = useCallback(
    (state: InputState, size: InputSize, text?: string) => {
      const textColor = InputStyleMap[state].enhancerColor;

      return text ? (
        <Layout centered {...InputLayoutMap[size].enhanced.textContainer}>
          <Text
            variant="inline_14_default"
            textColor={textColor}
            align="center">
            {text}
          </Text>
        </Layout>
      ) : null;
    },
    [],
  );

  const renderIcon = useCallback(
    (
      state: InputState,
      side: EnhancementSide,
      icon?: IconNames,
      iconSize?: InputIconSize,
      filled?: boolean,
      onPress?: () => void,
    ) => {
      const layout =
        IconLayoutMap[
          assert(iconSize, 'Icon size must have given value or default')
        ];
      const paddingLeft =
        layout?.inside || layout?.outside
          ? side === 'left'
            ? layout.outside
            : layout.inside
          : undefined;
      const paddingRight =
        layout?.inside || layout?.outside
          ? side === 'left'
            ? layout.inside
            : layout.outside
          : undefined;
      const iconState = state === 'Disabled' ? state : 'Default';

      return icon ? (
        <Layout
          paddingLeft={paddingLeft}
          paddingRight={paddingRight}
          onPress={onPress}>
          <Icon name={icon} filled={filled} size={iconSize} state={iconState} />
        </Layout>
      ) : null;
    },
    [],
  );

  const renderSpinner = useCallback(
    (spinner: boolean) =>
      spinner ? (
        <Layout>
          <Spinner />
        </Layout>
      ) : null,
    [],
  );

  const renderAvatar = useCallback(
    (avatar?: {uri: string; name: string}, onPress?: () => void) =>
      avatar ? (
        <Layout onPress={onPress}>
          <Avatar {...avatar} size="24" />
        </Layout>
      ) : null,
    [],
  );

  return (
    <BaseLayout {...baseInputContainerProps} width={inputWidth} align="center">
      {LeftEnhancer}
      {renderAvatar(
        leftAvatarUri
          ? {
              uri: leftAvatarUri,
              name: assert(
                leftAvatarName,
                'Input: leftAvatarName must be set when using leftAvatarUri',
              ),
            }
          : undefined,
        leftOnPress,
      )}
      {renderStaticText(state, size, leftText)}
      {renderIcon(
        state,
        'left',
        leftIcon,
        leftIconSize,
        leftIconFilled,
        leftOnPress,
      )}
      {renderSpinner(leftSpinner)}
      <BaseInput
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        {...baseInputProps}
      />
      {renderSpinner(rightSpinner)}
      {renderIcon(
        state,
        'right',
        rightIcon,
        rightIconSize,
        rightIconFilled,
        rightOnPress,
      )}
      {renderStaticText(state, size, rightText)}
      {renderAvatar(
        rightAvatarUri
          ? {
              uri: rightAvatarUri,
              name: assert(
                rightAvatarName,
                'Input: rightAvatarName must be set when using rightAvatarUri',
              ),
            }
          : undefined,
        rightOnPress,
      )}
      {RightEnhancer}
    </BaseLayout>
  );
};
